import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {NgClass} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {SwiperContainer, register} from "swiper/element/bundle";
import type {Swiper, SwiperOptions} from "swiper/types";
import {WorkEntitySuggestion} from "src/app/modules/shared/interfaces/WorkEntitySuggestion.interface";
import {WorksService} from "src/app/modules/shared/services/works.service";
import {UserAvatarComponent} from "src/app/modules/shared/components/user-avatar/user-avatar.component";
import {PointerHoverDirective} from "src/app/modules/shared/directives/pointer-hover.directive";
import {getEntitySuggestionKey} from "src/app/utils/entity-tag.util";
import {getHorizontalOverflowMask} from "src/app/utils/overflow-mask.util";
import {
  getEntityRoleLabelKey,
  getEntityScopeLabelKey,
} from "src/app/utils/entity-presentation.util";

@Component({
  selector: "app-entity-tags",
  templateUrl: "./entity-tags.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    TranslateModule,
    UserAvatarComponent,
    PointerHoverDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EntityTagsComponent implements AfterViewInit, OnChanges {
  readonly getEntitySuggestionKey = getEntitySuggestionKey;
  readonly roleLabelKey = getEntityRoleLabelKey;
  readonly scopeLabelKey = getEntityScopeLabelKey;
  readonly getHorizontalOverflowMask = getHorizontalOverflowMask;
  overflowStart = false;
  overflowEnd = false;
  @Input() headingKey = "";
  @Input() isMobile = false;
  @Input() entities: WorkEntitySuggestion[] = [];
  @Input() selected: WorkEntitySuggestion[] = [];
  @Output() toggled = new EventEmitter<WorkEntitySuggestion>();
  @ViewChild("entitySwiper") entitySwiper!: ElementRef<SwiperContainer>;
  @ViewChild("previousButton") previousButton!: ElementRef<HTMLButtonElement>;
  @ViewChild("nextButton") nextButton!: ElementRef<HTMLButtonElement>;

  constructor(
    private readonly worksService: WorksService,
    private readonly cdr: ChangeDetectorRef
  ) {
    register();
  }

  ngAfterViewInit(): void {
    const swiper = this.entitySwiper.nativeElement;
    Object.assign(swiper, {
      ...this.getResponsiveSwiperParams(),
      autoHeight: true,
      breakpointsBase: "container",
      speed: 300,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      navigation: {
        prevEl: this.previousButton.nativeElement,
        nextEl: this.nextButton.nativeElement,
      },
      pagination: false,
      on: {
        afterInit: this.updateOverflowEdges,
        progress: this.updateOverflowEdges,
        lock: this.updateOverflowEdges,
        unlock: this.updateOverflowEdges,
      },
    });
    swiper.initialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const swiper = this.entitySwiper?.nativeElement.swiper;
    if (!changes["isMobile"] || !swiper) return;
    const params = this.getResponsiveSwiperParams();
    Object.assign(swiper.params, params);
    Object.assign(swiper.originalParams, params);
    swiper.currentBreakpoint = undefined;
    swiper.update();
  }

  private getResponsiveSwiperParams(): SwiperOptions {
    return {
      slidesPerView: this.isMobile ? 2 : 1.4,
      slidesPerGroup: this.isMobile ? 2 : 1,
      spaceBetween: this.isMobile ? 12 : 8,
      breakpoints: this.isMobile
        ? {}
        : {
            456: {slidesPerView: 2, slidesPerGroup: 2},
            684: {slidesPerView: 3, slidesPerGroup: 3},
            912: {slidesPerView: 4, slidesPerGroup: 4},
          },
    };
  }

  private readonly updateOverflowEdges = (swiper: Swiper): void => {
    const start = !swiper.isBeginning && !swiper.isLocked;
    const end = !swiper.isEnd && !swiper.isLocked;
    if (start === this.overflowStart && end === this.overflowEnd) return;
    this.overflowStart = start;
    this.overflowEnd = end;
    this.cdr.detectChanges();
  };

  isSelected(entity: WorkEntitySuggestion): boolean {
    return this.selected.some(
      (selected) =>
        getEntitySuggestionKey(selected) === getEntitySuggestionKey(entity)
    );
  }

  getPicture(entity: WorkEntitySuggestion): string | null {
    return entity.picture
      ? this.worksService.addSizeToFileName(entity.picture, "small")
      : null;
  }
}
